import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  CContainer,
  CRow,
  CCol,
  CCard,
  CCardBody,
  CCardHeader,
  CButton,
  CSpinner,
  CAlert,
  CTable,
  CBadge,
  CFormInput,
  CInputGroup,
  CInputGroupText,
  CNav,
  CNavItem,
  CNavLink,
  CTabContent,
  CTabPane,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CFormCheck,
  CFormSelect,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import {
  cilArrowLeft,
  cilPlus,
  cilSearch,
  cilDollar,
  cilCheckCircle,
  cilWarning,
  cilStorage,
  cilEnvelopeClosed,
} from "@coreui/icons";
import { resellerAPI, superAdminAPI } from "../services/api";
import quickbooksService from "../services/quickbooksService";

const ClientProducts = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("commercial");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Client state
  const [client, setClient] = useState(location.state?.client || null);
  // Reseller state
  const [reseller, setReseller] = useState(null);
  // User state for logged-in user info
  const [currentUser, setCurrentUser] = useState(null);
  
  // Get user type from location state or determine from token/context
  const isSuperAdmin = location.state?.isSuperAdmin || false;
  const resellerIdFromState = location.state?.resellerId;

  // QuickBooks Modal States
  const [showQuickBooksModal, setShowQuickBooksModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [availableSupportProducts, setAvailableSupportProducts] = useState([]);
  const [availableCartridgeProducts, setAvailableCartridgeProducts] = useState([]);
  const [selectedSupportProductId, setSelectedSupportProductId] = useState("");
  const [selectedCartridgeProductId, setSelectedCartridgeProductId] = useState("");
  const [includeSupport, setIncludeSupport] = useState(false);
  const [includeCartridge, setIncludeCartridge] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [quickbooksLoading, setQuickbooksLoading] = useState(false);
  const [quickbooksError, setQuickbooksError] = useState("");

  // ✅ AUTOMATIC PAYMENT TERMS FROM RESELLER PROFILE
  const [paymentTerms, setPaymentTerms] = useState("immediate");

  const [isTaxExempt, setIsTaxExempt] = useState(false);
  const [taxExemptReason, setTaxExemptReason] = useState("");

  useEffect(() => {
    fetchProducts();
    fetchResellerProfile();
    fetchCurrentUser();
    if (!client) {
      fetchClientDetails();
    }
  }, []);

  useEffect(() => {
    filterProducts();
  }, [searchTerm, products, activeTab]);

  const fetchCurrentUser = async () => {
    try {
      // Get user info from localStorage (set during login)
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      if (token) {
        // Try to decode JWT token to get user info
        try {
          const base64Url = token.split('.')[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          }).join(''));
          
          const userData = JSON.parse(jsonPayload);
          setCurrentUser(userData);
          console.log("👤 Current user from token:", userData);
        } catch (e) {
          console.log("⚠️ Could not decode token:", e);
        }
      }
      
      // Also try to get from API
      if (!isSuperAdmin) {
        try {
          const response = await resellerAPI.getProfile();
          if (response.data.success) {
            const userInfo = response.data.user || {};
            setCurrentUser(prev => ({ ...prev, ...userInfo }));
            console.log("👤 Current user from API:", userInfo);
            
            // Save to localStorage for future use
            localStorage.setItem("userInfo", JSON.stringify(userInfo));
          }
        } catch (error) {
          console.log("⚠️ Could not get user from API:", error);
        }
      }
    } catch (error) {
      console.error("Error fetching current user:", error);
    }
  };

  const fetchResellerProfile = async () => {
    try {
      // For super admin, we need to fetch reseller info differently
      if (isSuperAdmin) {
        if (!resellerIdFromState) {
          console.warn("Super admin doesn't have resellerId in state");
          setReseller(null);
          setPaymentTerms("immediate");
          return;
        }
        
        try {
          // Super admin uses different API endpoint
          const response = await superAdminAPI.getSingleReseller(resellerIdFromState);
          if (response.data.success) {
            const resellerData = response.data.reseller;
            setReseller(resellerData);
            
            // ✅ AUTOMATICALLY SET PAYMENT TERMS BASED ON RESELLER'S PAYMENT_MODE
            const paymentMode = resellerData.payment_mode;
            let terms = "immediate";
            
            switch(paymentMode) {
              case "Credit Card":
              case "ACH":
                terms = "immediate";
                break;
              case "Net 30":
                terms = "net_30";
                break;
              case "Net 45":
                terms = "net_45";
                break;
              case "Net 60":
                terms = "net_60";
                break;
              case "Net 65":
                terms = "net_60"; // Fallback for Net 65
                break;
              default:
                terms = "immediate";
            }
            
            setPaymentTerms(terms);
            console.log(`✅ Auto-set payment terms to: ${terms} based on reseller payment mode: ${paymentMode}`);
            
            console.log("📧 Reseller email fetched for super admin:", resellerData.email);
          }
        } catch (error) {
          console.error("Super admin error fetching reseller profile:", error);
          setReseller(null);
          setPaymentTerms("immediate");
        }
      } else {
        // Regular reseller uses their own profile endpoint
        const response = await resellerAPI.getProfile();
        if (response.data.success) {
          setReseller(response.data.reseller);
          
          // ✅ AUTOMATICALLY SET PAYMENT TERMS BASED ON RESELLER'S PAYMENT_MODE
          const paymentMode = response.data.reseller.payment_mode;
          let terms = "immediate";
          
          switch(paymentMode) {
            case "Credit Card":
            case "ACH":
              terms = "immediate";
              break;
            case "Net 30":
              terms = "net_30";
              break;
            case "Net 45":
              terms = "net_45";
              break;
            case "Net 60":
              terms = "net_60";
              break;
            case "Net 65":
              terms = "net_60"; // Fallback for Net 65
              break;
            default:
              terms = "immediate";
          }
          
          setPaymentTerms(terms);
          console.log(`✅ Auto-set payment terms to: ${terms} based on reseller payment mode: ${paymentMode}`);
          
          console.log("📧 Reseller email fetched for regular user:", response.data.reseller.email);
        }
      }
    } catch (error) {
      console.error("Error fetching reseller profile:", error);
      setPaymentTerms("immediate");
    }
  };

  const fetchClientDetails = async () => {
    try {
      let response;
      if (isSuperAdmin) {
        // Super admin gets client details from super admin API
        response = await superAdminAPI.getSingleClient(clientId);
      } else {
        // Regular reseller gets client details from reseller API
        response = await resellerAPI.getMyClients();
      }
      
      if (response.data.success) {
        if (isSuperAdmin) {
          setClient(response.data.client);
        } else {
          const clients = response.data.clients;
          const currentClient = clients.find(
            (c) =>
              c.id === clientId || c.clientid === clientId || c._id === clientId
          );
          if (currentClient) {
            setClient(currentClient);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching client details:", error);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError("");

      let response;
      if (isSuperAdmin) {
        // Super admin uses super admin API to get products
        response = await superAdminAPI.getSoftwareProducts();
      } else {
        // Regular reseller uses reseller API
        response = await resellerAPI.getAllProducts();
      }

      if (response.data.success) {
        setProducts(response.data.products);
      } else {
        setError("Failed to fetch products");
      }
    } catch (err) {
      console.error("Error fetching products:", err);
      setError("Failed to load products. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const filterProducts = () => {
    let filtered = products;

    // Handle case-insensitive market filtering
    if (activeTab === "gsa") {
      filtered = filtered.filter(
        (product) => product.market && product.market.toLowerCase() === "gsa"
      );
    } else if (activeTab === "commercial") {
      filtered = filtered.filter(
        (product) =>
          product.market && product.market.toLowerCase() === "commercial"
      );
    }

    if (searchTerm) {
      filtered = filtered.filter(
        (product) =>
          product.software_name
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          product.subscription
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          product.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.softwareid?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    filtered.sort((a, b) => {
      const typeOrder = { NEW: 1, Support: 2, Cartridge: 3 };
      return typeOrder[a.type] - typeOrder[b.type];
    });

    setFilteredProducts(filtered);
  };

  // Check if product is unlimited
  const isUnlimitedProduct = (product) => {
    if (!product) return false;

    const subscription = product.subscription || "";
    return subscription.toLowerCase().includes("unlimited");
  };

  // Check if product can have cartridges (not unlimited)
  const canHaveCartridges = (product) => {
    if (!product) return false;

    // Only non-unlimited products can have cartridges
    return !isUnlimitedProduct(product);
  };

  // Get support products for the current market (case-insensitive)
  const getAvailableSupportProducts = () => {
    // Get support products from the current market tab (case-insensitive)
    const supportProducts = products.filter((p) => {
      const isSupport = p.type === "Support";
      const isCurrentMarket =
        p.market && p.market.toLowerCase() === activeTab.toLowerCase();

      return isSupport && isCurrentMarket;
    });

    return supportProducts;
  };

  // Get cartridge products for the current market (case-insensitive)
  const getAvailableCartridgeProducts = () => {
    // Get cartridge products from the current market tab (case-insensitive)
    const cartridgeProducts = products.filter((p) => {
      const isCartridge = p.type === "Cartridge";
      const isCurrentMarket =
        p.market && p.market.toLowerCase() === activeTab.toLowerCase();

      return isCartridge && isCurrentMarket;
    });

    return cartridgeProducts;
  };

  // Calculate partner discount amounts
  const calculatePartnerAmount = (customerAmount) => {
    if (!reseller || !reseller.margin) return customerAmount;

    const margin = reseller.margin;
    const discountRate = margin / 100;
    return customerAmount * (1 - discountRate);
  };

  const calculateDiscountAmount = (customerAmount) => {
    if (!reseller || !reseller.margin) return 0;

    const margin = reseller.margin;
    const discountRate = margin / 100;
    return customerAmount * discountRate;
  };

  // Calculate renewal amount based on product type and addons
  const calculateRenewalAmount = (
    mainProduct,
    supportProduct,
    cartridgeProduct
  ) => {
    if (!mainProduct) return 0;

    const isUnlimited = isUnlimitedProduct(mainProduct);
    const mainProductPrice = mainProduct.price || 0;
    const supportPrice = supportProduct?.price || 0;
    const cartridgePrice = cartridgeProduct?.price || 0;

    if (isUnlimited) {
      // Unlimited product: if has addons, only addons renew; otherwise full product renews
      return supportProduct || cartridgeProduct
        ? supportPrice + cartridgePrice
        : mainProductPrice;
    } else {
      // Limited product: only addons renew
      return supportPrice + cartridgePrice;
    }
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case "NEW":
        return "success";
      case "Support":
        return "info";
      case "Cartridge":
        return "warning";
      default:
        return "secondary";
    }
  };

  const getSubscriptionTypeBadge = (subscriptionType) => {
    switch (subscriptionType) {
      case "Perpetual":
        return "dark";
      case "Annual":
        return "primary";
      default:
        return "secondary";
    }
  };

  const formatPrice = (price) => {
    if (!price && price !== 0) return "$0.00";

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(price);
  };

  // Calculate market-specific products (case-insensitive)
  const gsaProducts = products.filter(
    (p) => p.market && p.market.toLowerCase() === "gsa"
  );
  const commercialProducts = products.filter(
    (p) => p.market && p.market.toLowerCase() === "commercial"
  );

  // Group products by type for current market
  const groupProductsByType = (productsToGroup) => {
    const grouped = {
      NEW: productsToGroup.filter((p) => p.type === "NEW"),
      Support: productsToGroup.filter((p) => p.type === "Support"),
      Cartridge: productsToGroup.filter((p) => p.type === "Cartridge"),
    };

    // Sort NEW products: Annual first, then Perpetual
    if (grouped["NEW"].length > 0) {
      grouped["NEW"].sort((a, b) => {
        if (
          a.subscription_type === "Annual" &&
          b.subscription_type !== "Annual"
        )
          return -1;
        if (
          a.subscription_type !== "Annual" &&
          b.subscription_type === "Annual"
        )
          return 1;
        return a.software_name.localeCompare(b.software_name);
      });
    }

    return grouped;
  };

  const groupedCommercialProducts = groupProductsByType(commercialProducts);
  const groupedGsaProducts = groupProductsByType(gsaProducts);

  // QuickBooks Functions
  const handleAddToClient = (product) => {
    if (!client) {
      setQuickbooksError("Client information not loaded. Please try again.");
      return;
    }

    setSelectedProduct(product);

    // Get support products for the current market
    const supportProducts = getAvailableSupportProducts();
    setAvailableSupportProducts(supportProducts);

    // Get cartridge products for the current market (only if product can have cartridges)
    const cartridgeProducts = canHaveCartridges(product)
      ? getAvailableCartridgeProducts()
      : [];
    setAvailableCartridgeProducts(cartridgeProducts);

    // Reset all states
    setIncludeSupport(false);
    setIncludeCartridge(false);
    setSelectedSupportProductId("");
    setSelectedCartridgeProductId("");
    setInvoiceData(null);
    setQuickbooksError("");
    setIsTaxExempt(false);
    setTaxExemptReason("");

    setShowQuickBooksModal(true);
  };

  const handleSupportToggle = (checked) => {
    setIncludeSupport(checked);
    if (!checked) {
      setSelectedSupportProductId("");
    } else if (
      availableSupportProducts.length > 0 &&
      !selectedSupportProductId
    ) {
      // Auto-select first support product if none selected
      setSelectedSupportProductId(availableSupportProducts[0].softwareid);
    }
  };

  const handleCartridgeToggle = (checked) => {
    setIncludeCartridge(checked);
    if (!checked) {
      setSelectedCartridgeProductId("");
    } else if (
      availableCartridgeProducts.length > 0 &&
      !selectedCartridgeProductId
    ) {
      // Auto-select first cartridge product if none selected
      setSelectedCartridgeProductId(availableCartridgeProducts[0].softwareid);
    }
  };

  const getSelectedSupportProduct = () => {
    return availableSupportProducts.find(
      (p) => p.softwareid === selectedSupportProductId
    );
  };

  const getSelectedCartridgeProduct = () => {
    return availableCartridgeProducts.find(
      (p) => p.softwareid === selectedCartridgeProductId
    );
  };

  // ✅ CORRECTED: Get partner email - ALWAYS use RESELLER email for billing
  const getPartnerEmail = () => {
    let partnerEmail = "";
    
    console.log("🔄 Determining partner email for billing:");
    console.log("   isSuperAdmin:", isSuperAdmin);
    console.log("   reseller object:", reseller);
    console.log("   currentUser:", currentUser);
    
    // ✅ IMPORTANT: ALWAYS use reseller's email for billing, NOT logged-in user's email
    
    // 1. FIRST PRIORITY: Use reseller's email from reseller object
    if (reseller) {
      partnerEmail = reseller.email || reseller.contact_email || "";
      console.log("📧 Using reseller email from reseller object:", partnerEmail);
    }
    
    // 2. SECOND PRIORITY: For super admin, try to get from client's reseller info
    if (!partnerEmail && isSuperAdmin && client) {
      partnerEmail = client.reseller_contact_email || "";
      console.log("📧 Super admin using client's reseller contact email:", partnerEmail);
    }
    
    // 3. THIRD PRIORITY: Try to get from API call (fallback)
    if (!partnerEmail && !isSuperAdmin) {
      try {
        const storedUser = JSON.parse(localStorage.getItem("userInfo") || "{}");
        if (storedUser && storedUser.email) {
          partnerEmail = storedUser.email;
          console.log("📧 Using email from localStorage userInfo:", partnerEmail);
        }
      } catch (e) {
        console.log("⚠️ Could not parse userInfo from localStorage");
      }
    }
    
    // 4. FINAL FALLBACK: Use a default email
    if (!partnerEmail || partnerEmail === "undefined" || partnerEmail === "") {
      partnerEmail = "billing@example.com";
      console.log("⚠️ Using fallback partner email:", partnerEmail);
    }
    
    // ✅ CRITICAL: Log warning if using logged-in user's email for super admin
    if (isSuperAdmin && currentUser?.email && partnerEmail === currentUser.email) {
      console.warn("⚠️ WARNING: Using super admin's login email instead of reseller email!");
    }
    
    console.log("✅ Final partner email for billing:", partnerEmail);
    return partnerEmail;
  };

  // ✅ CORRECTED: QuickBooks Submit Handler - Uses RESELLER email for ALL users
  const handleQuickBooksSubmit = async () => {
    if (!selectedProduct || !client) {
      setQuickbooksError("Product or client information is missing. Please try again.");
      return;
    }

    try {
      setQuickbooksLoading(true);
      setQuickbooksError("");

      // Calculate total amounts
      let totalCustomerAmount = selectedProduct.price;
      let totalPartnerAmount = calculatePartnerAmount(selectedProduct.price);
      const selectedSupport = getSelectedSupportProduct();
      const selectedCartridge = getSelectedCartridgeProduct();

      if (includeSupport && selectedSupport) {
        totalCustomerAmount += selectedSupport.price;
        totalPartnerAmount += calculatePartnerAmount(selectedSupport.price);
      }

      if (includeCartridge && selectedCartridge) {
        totalCustomerAmount += selectedCartridge.price;
        totalPartnerAmount += calculatePartnerAmount(selectedCartridge.price);
      }

      const discountAmount = calculateDiscountAmount(totalCustomerAmount);
      const resellerMargin = reseller?.margin || 0;

      // ✅ Use automatic payment terms from reseller profile
      const automaticPaymentTerms = paymentTerms;

      // ✅ CRITICAL FIX: Get partner email - ALWAYS use RESELLER's email, not logged-in user's email
      const partnerEmail = getPartnerEmail();
      
      // Get customer email (for reference only)
      const customerEmail = client.client_owner_email || client.reseller_contact_email || "";
      
      // ✅ ALWAYS use partner (reseller) email for billing
      const billingEmail = partnerEmail;

      console.log("📧 Email configuration for QuickBooks:");
      console.log(`   isSuperAdmin: ${isSuperAdmin}`);
      console.log(`   Payment Terms: ${automaticPaymentTerms}`);
      console.log(`   Customer Email (reference): ${customerEmail}`);
      console.log(`   Partner Email (RESELLER): ${partnerEmail}`);
      console.log(`   Billing Email (ALWAYS RESELLER): ${billingEmail}`);
      console.log(`   Reseller Name: ${reseller?.name || 'N/A'}`);
      console.log(`   Reseller ID: ${reseller?._id || resellerIdFromState || 'N/A'}`);

      // Prepare data with automatic payment terms
      const requestData = {
        subscription_number: `${Date.now()}`,
        account_name: client.name || `Client ${clientId}`,
        // product_name: selectedProduct.software_name,
        product_name: selectedProduct.subscription,
        amount: totalPartnerAmount,
        customer_amount: totalCustomerAmount,
        partner_amount: totalPartnerAmount,
        discount: discountAmount,
        reseller_margin: resellerMargin,
        reseller_name: reseller?.name || "Reseller",
        storage_option: "NONE",
        storage_price: 0,
        is_new_purchase: true,
        product_id: selectedProduct.softwareid,
        support_product_id:
          includeSupport && selectedSupport ? selectedSupport.softwareid : null,
        cartridge_product_id:
          includeCartridge && selectedCartridge ? selectedCartridge.softwareid : null,
        client_id: client.id || client.clientid || clientId,
        // ✅ Email configuration - ALWAYS use RESELLER email for billing
        customer_email: customerEmail,          // End user email (for reference)
        partner_email: partnerEmail,            // ✅ RESELLER email (not logged-in user)
        billing_email: billingEmail,            // ✅ ALWAYS reseller email for billing
        // Billing address
        billing_street: client.billing_street,
        billing_city: client.billing_city,
        billing_state: client.billing_state,
        billing_country: client.billing_country,
        billing_zip: client.billingzipcode,
        // Tax exemption
        is_tax_exempt: isTaxExempt,
        tax_exempt_reason: taxExemptReason,
        // ✅ Use automatic payment terms
        payment_terms: automaticPaymentTerms,
      };

      // ✅ ADD RESELLER ID FOR SUPER ADMIN (CRITICAL)
      if (isSuperAdmin && resellerIdFromState) {
        requestData.reseller_id = resellerIdFromState;
        console.log(`✅ Added reseller_id for super admin: ${resellerIdFromState}`);
      }

      console.log("📦 Request data to QuickBooks:", requestData);

      const response = await quickbooksService.createInvoice(requestData);

      if (response.success) {
        // Handle both response types
        let invoiceDataToSet = null;

        if (response.invoice_data) {
          // Type 1: QuickBooks invoice response
          invoiceDataToSet = {
            ...response.invoice_data,
            is_net_terms: automaticPaymentTerms !== "immediate",
            payment_terms: automaticPaymentTerms,
            initial_amount_partner: totalPartnerAmount,
            initial_amount_customer: totalCustomerAmount,
            initial_discount_amount: discountAmount,
          };
        } else if (response.order) {
          // Type 2: Database order response
          invoiceDataToSet = {
            is_net_terms: automaticPaymentTerms !== "immediate",
            payment_terms: automaticPaymentTerms,
            initial_amount_partner: response.order.initial_amount_partner,
            initial_amount_customer: response.order.initial_amount_customer,
            initial_discount_amount: response.order.initial_discount_amount,
            payment_due_date: response.order.payment_due_date,
            invoice_id: response.order.id,
            invoice_number: response.order.subscription_number,
            payment_link: null, // No payment link for net terms
            amounts: {
              customer_amount: response.order.initial_amount_customer,
              discount_amount: response.order.initial_discount_amount,
              partner_amount: response.order.initial_amount_partner,
              total_due: response.order.initial_amount_partner,
            },
          };
        }

        setInvoiceData(invoiceDataToSet);

        // Show success message with email info
        console.log(`✅ ALL billing emails will be sent to RESELLER: ${partnerEmail}`);

        // Start polling for payment status only for immediate payments with payment link
        if (automaticPaymentTerms === "immediate" && invoiceDataToSet.payment_link) {
          const pollInterval = setInterval(() => {
            checkPaymentStatus(invoiceDataToSet.invoice_id).then((status) => {
              if (status === "paid") {
                clearInterval(pollInterval);
              }
            });
          }, 5000);

          setTimeout(() => {
            clearInterval(pollInterval);
          }, 10 * 60 * 1000);
        }
      } else {
        setQuickbooksError(response.error || "Failed to create order");
      }
    } catch (err) {
      console.error("Order creation error:", err);
      setQuickbooksError(
        err.response?.data?.error || "Failed to create order. Please try again."
      );
    } finally {
      setQuickbooksLoading(false);
    }
  };

  const handleDirectPayment = () => {
    if (invoiceData?.payment_link) {
      window.open(invoiceData.payment_link, "_blank", "noopener,noreferrer");
    }
  };

  const checkPaymentStatus = async (invoiceId) => {
    try {
      const response = await quickbooksService.checkPaymentStatus(invoiceId);

      if (response.success) {
        if (response.payment_status === "paid") {
          navigate("/ThankYouPage", {
            state: {
              invoiceId: invoiceId,
              invoiceNumber: response.invoice_number,
              customerName: response.customer_name,
              amounts: response.amounts,
              isRenewal: false,
            },
          });
          return "paid";
        }
        return response.payment_status;
      }
      return "unknown";
    } catch (error) {
      console.error("Error checking payment status:", error);
      return "error";
    }
  };

  const handleCloseQuickBooksModal = () => {
    setShowQuickBooksModal(false);
    setSelectedProduct(null);
    setAvailableSupportProducts([]);
    setAvailableCartridgeProducts([]);
    setIncludeSupport(false);
    setIncludeCartridge(false);
    setSelectedSupportProductId("");
    setSelectedCartridgeProductId("");
    setInvoiceData(null);
    setQuickbooksError("");
    setIsTaxExempt(false);
    setTaxExemptReason("");
  };

  const isPerpetualLicense = selectedProduct?.subscription_type === "Perpetual";
  const selectedSupportProduct = getSelectedSupportProduct();
  const selectedCartridgeProduct = getSelectedCartridgeProduct();
  const showSupportSection =
    isPerpetualLicense && availableSupportProducts.length > 0;
  const showCartridgeSection =
    canHaveCartridges(selectedProduct) && availableCartridgeProducts.length > 0;
  const isUnlimited = isUnlimitedProduct(selectedProduct);

  // Calculate total amounts for display
  const totalCustomerAmount = selectedProduct
    ? selectedProduct.price +
      (includeSupport && selectedSupportProduct
        ? selectedSupportProduct.price
        : 0) +
      (includeCartridge && selectedCartridgeProduct
        ? selectedCartridgeProduct.price
        : 0)
    : 0;

  const totalPartnerAmount = calculatePartnerAmount(totalCustomerAmount);
  const totalDiscountAmount = calculateDiscountAmount(totalCustomerAmount);

  // ✅ Helper to display email - Shows RESELLER email, not logged-in user
  const getDisplayEmail = () => {
    // Always show reseller email, not logged-in user's email
    if (reseller?.email) {
      return reseller.email;
    }
    if (reseller?.contact_email) {
      return reseller.contact_email;
    }
    if (client?.reseller_contact_email) {
      return client.reseller_contact_email;
    }
    return "N/A";
  };

  const renderGroupedProductsTable = (groupedProducts, market) => {
    const types = ["NEW", "Support", "Cartridge"];

    return (
      <div>
        {types.map((type) => {
          const productsOfType = groupedProducts[type];
          if (productsOfType.length === 0) return null;

          return (
            <div key={type} className="mb-4">
              <div className="d-flex align-items-center mb-2">
                <CBadge color={getTypeBadge(type)} className="fs-6 px-3 py-2">
                  {type} Products ({productsOfType.length})
                </CBadge>
              </div>
              <CTable responsive hover>
                <CTableHead>
                  <CTableRow>
                    <CTableHeaderCell>Software ID</CTableHeaderCell>
                    <CTableHeaderCell>Product Name</CTableHeaderCell>
                    <CTableHeaderCell>Subscription</CTableHeaderCell>
                    <CTableHeaderCell>Type</CTableHeaderCell>
                    <CTableHeaderCell>Subscription Type</CTableHeaderCell>
                    <CTableHeaderCell>Price</CTableHeaderCell>
                    <CTableHeaderCell>Action</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {productsOfType.map((product) => (
                    <CTableRow key={product.id}>
                      <CTableDataCell>
                        <code>{product.softwareid}</code>
                      </CTableDataCell>
                      <CTableDataCell>
                        <strong>{product.software_name}</strong>
                        <br />
                        <div className="mt-1">
                          {product.subscription_type === "Perpetual" && (
                            <CBadge color="warning" className="me-2">
                              Can Add Support
                            </CBadge>
                          )}
                          {!isUnlimitedProduct(product) &&
                            product.type === "NEW" && (
                              <CBadge color="info">Can Add Cartridge</CBadge>
                            )}
                        </div>
                      </CTableDataCell>
                      <CTableDataCell>
                        <div
                          className="text-wrap"
                          style={{ maxWidth: "300px" }}
                        >
                          {product.subscription}
                        </div>
                      </CTableDataCell>
                      <CTableDataCell>
                        <CBadge color={getTypeBadge(product.type)}>
                          {product.type}
                        </CBadge>
                      </CTableDataCell>
                      <CTableDataCell>
                        <CBadge
                          color={getSubscriptionTypeBadge(
                            product.subscription_type
                          )}
                        >
                          {product.subscription_type}
                        </CBadge>
                      </CTableDataCell>
                      <CTableDataCell>
                        <div className="d-flex align-items-center">
                          <CIcon
                            icon={cilDollar}
                            className="text-success me-1"
                          />
                          <strong>{formatPrice(product.price)}</strong>
                          {reseller && (
                            <small className="text-muted ms-2">
                              (Partner:{" "}
                              {formatPrice(
                                calculatePartnerAmount(product.price)
                              )}
                              )
                            </small>
                          )}
                        </div>
                      </CTableDataCell>
                      <CTableDataCell>
                        <CButton
                          color="primary"
                          size="sm"
                          onClick={() => handleAddToClient(product)}
                          disabled={!client}
                        >
                          <CIcon icon={cilPlus} className="me-1" />
                          Add to Client
                        </CButton>
                      </CTableDataCell>
                    </CTableRow>
                  ))}
                </CTableBody>
              </CTable>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <CContainer className="d-flex justify-content-center align-items-center min-vh-50">
        <CSpinner color="primary" />
      </CContainer>
    );
  }

  if (error) {
    return (
      <CContainer>
        <CAlert color="danger" className="mb-4">
          {error}
          <CButton
            color="primary"
            size="sm"
            className="ms-3"
            onClick={fetchProducts}
          >
            Retry
          </CButton>
        </CAlert>
      </CContainer>
    );
  }

  return (
    <CContainer fluid>
      {/* Back Button */}
      <CButton
        color="outline-primary"
        className="mb-3"
        onClick={() => isSuperAdmin ? navigate("/superadmin/clients") : navigate("/clients")}
      >
        <CIcon icon={cilArrowLeft} className="me-2" />
        Back to {isSuperAdmin ? "Super Admin Clients" : "Clients"}
      </CButton>

      {/* Page Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>Add Products to Client</h2>
          <p className="text-muted">
            {client
              ? `Select products to add to ${client.name}`
              : `Select products to add to client #${clientId}`}
          </p>
          {!client && (
            <CAlert color="warning" className="mt-2">
              <small>
                Client information loading... Some features may be limited.
              </small>
            </CAlert>
          )}
        </div>
        <div className="d-flex align-items-center gap-3">
          {client && (
            <CBadge color="info" className="fs-6">
              Client: {client.name}
            </CBadge>
          )}
          {/* ✅ Show reseller payment mode in header if available */}
          {reseller && (
            <CBadge color="success" className="fs-6">
              Payment Mode: {reseller.payment_mode}
            </CBadge>
          )}
          {isSuperAdmin && (
            <CBadge color="warning" className="fs-6">
              Super Admin Mode
            </CBadge>
          )}
          {/* ✅ Show RESELLER email in header (not logged-in user) */}
          {getDisplayEmail() !== "N/A" && (
            <CBadge color="primary" className="fs-6">
              <CIcon icon={cilEnvelopeClosed} className="me-1" />
              Billing Email: {getDisplayEmail()}
            </CBadge>
          )}
        </div>
      </div>

      {/* Products Catalog */}
      <CCard>
        <CCardHeader>
          <CRow className="align-items-center">
            <CCol md={6}>
              <CNav variant="tabs">
                <CNavItem>
                  <CNavLink
                    active={activeTab === "commercial"}
                    onClick={() => setActiveTab("commercial")}
                  >
                    Commercial Market ({commercialProducts.length})
                  </CNavLink>
                </CNavItem>
                <CNavItem>
                  <CNavLink
                    active={activeTab === "gsa"}
                    onClick={() => setActiveTab("gsa")}
                  >
                    GSA Market ({gsaProducts.length})
                  </CNavLink>
                </CNavItem>
              </CNav>
            </CCol>
            <CCol md={6}>
              <CInputGroup>
                <CInputGroupText>
                  <CIcon icon={cilSearch} />
                </CInputGroupText>
                <CFormInput
                  placeholder={`Search ${activeTab} products...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <CButton
                    color="secondary"
                    variant="outline"
                    onClick={() => setSearchTerm("")}
                  >
                    Clear
                  </CButton>
                )}
              </CInputGroup>
            </CCol>
          </CRow>
        </CCardHeader>
        <CCardBody>
          <CTabContent>
            <CTabPane visible={activeTab === "commercial"}>
              {filteredProducts.length > 0 ? (
                renderGroupedProductsTable(
                  groupProductsByType(filteredProducts),
                  "commercial"
                )
              ) : (
                <div className="text-center p-4">
                  {searchTerm ? (
                    <>
                      <p className="text-muted">
                        No commercial products found matching "{searchTerm}"
                      </p>
                      <CButton
                        color="primary"
                        onClick={() => setSearchTerm("")}
                      >
                        Clear Search
                      </CButton>
                    </>
                  ) : (
                    <p className="text-muted">
                      No commercial products available.
                    </p>
                  )}
                </div>
              )}
            </CTabPane>

            <CTabPane visible={activeTab === "gsa"}>
              {filteredProducts.length > 0 ? (
                renderGroupedProductsTable(
                  groupProductsByType(filteredProducts),
                  "gsa"
                )
              ) : (
                <div className="text-center p-4">
                  {searchTerm ? (
                    <>
                      <p className="text-muted">
                        No GSA products found matching "{searchTerm}"
                      </p>
                      <CButton
                        color="primary"
                        onClick={() => setSearchTerm("")}
                      >
                        Clear Search
                      </CButton>
                    </>
                  ) : (
                    <p className="text-muted">No GSA products available.</p>
                  )}
                </div>
              )}
            </CTabPane>
          </CTabContent>
        </CCardBody>
      </CCard>

      {/* QuickBooks Invoice Modal */}
      <CModal
        size="xl"
        visible={showQuickBooksModal}
        onClose={handleCloseQuickBooksModal}
      >
        <CModalHeader>
          <CModalTitle>
            <CIcon icon={cilPlus} className="me-2" />
            {invoiceData
              ? `Order Created - ${selectedProduct?.software_name}`
              : `Purchase Product - ${selectedProduct?.software_name}`}
          </CModalTitle>
        </CModalHeader>
        <CModalBody>
          {invoiceData ? (
            /* SUCCESS VIEW */
            <CRow>
              <CCol>
                {/* DIFFERENT MESSAGE FOR NET TERMS VS ACH AND CREDIT CARD */}
                {paymentTerms !== "immediate" ? (
                  <CAlert color="success" className="mb-4">
                    <h6>
                      ✅ Subscription{" "}
                      {invoiceData.is_net_terms ? "Renewed" : "Activated"}{" "}
                      Successfully!
                    </h6>
                    <p className="mb-1">
                      <strong>Payment Terms:</strong> {reseller?.payment_mode || "Net Terms"}
                    </p>
                    <p className="mb-1">
                      <strong>Invoice Number:</strong>{" "}
                      {invoiceData.invoice_number}
                    </p>
                    <p className="mb-1">
                      <strong>Original Price:</strong>{" "}
                      {formatPrice(invoiceData.amounts?.customer_amount)}
                    </p>
                    <p className="mb-1">
                      <strong>Partner Discount:</strong> -
                      {formatPrice(invoiceData.amounts?.discount_amount)} (
                      {reseller?.margin || 0}%)
                    </p>

                    {/* ✅ ADD TAX INFORMATION FOR NET TERMS */}
                    <p className="mb-1">
                      <strong>Sales Tax:</strong>{" "}
                      {invoiceData.is_tax_exempt ? (
                        <span className="text-success">$0.00 (Tax Exempt)</span>
                      ) : (
                        formatPrice(invoiceData.amounts?.tax_amount)
                      )}
                    </p>
                    {invoiceData.is_tax_exempt && (
                      <p className="mb-1">
                        <strong>Tax Exemption:</strong>{" "}
                        {invoiceData.tax_exempt_reason}
                      </p>
                    )}
                    <p className="mb-1">
                      <strong>Total Due:</strong>{" "}
                      {formatPrice(invoiceData.amounts?.total_due)}
                    </p>
                    <p className="mb-1">
                      <strong>Due Date:</strong>{" "}
                      {invoiceData.payment_due_date
                        ? new Date(
                            invoiceData.payment_due_date
                          ).toLocaleDateString()
                        : new Date(
                            Date.now() +
                              parseInt(paymentTerms.replace("net_", "")) *
                                24 *
                                60 *
                                60 *
                                1000
                          ).toLocaleDateString()}
                    </p>
                    {/* ✅ ADD EMAIL INFORMATION - ALWAYS RESELLER */}
                    <p className="mb-1">
                      <strong>Billing Emails To:</strong>{" "}
                      <span className="text-primary">
                        <CIcon icon={cilEnvelopeClosed} className="me-1" />
                        {getDisplayEmail()}
                      </span>
                      <small className="text-muted ms-2">
                        (ALL billing emails go to reseller)
                      </small>
                    </p>
                    <p className="mb-0 text-info">
                      <strong>
                        ✅ Your subscription is now active! Payment due in{" "}
                        {paymentTerms.replace("net_", "")} days.
                        <br />
                        All billing emails will be sent to the reseller.
                      </strong>
                    </p>
                  </CAlert>
                ) : (
                  <CAlert color="success" className="mb-4">
                    <h6>✅ Payment Ready!</h6>
                    <p className="mb-1">
                      <strong>Payment Terms:</strong> {reseller?.payment_mode || "ACH and Credit Card"}
                    </p>
                    <p className="mb-1">
                      <strong>Invoice Number:</strong>{" "}
                      {invoiceData.invoice_number || invoiceData.invoice_id}
                    </p>
                    <p className="mb-1">
                      <strong>Original Price:</strong>{" "}
                      {formatPrice(
                        invoiceData.amounts?.customer_amount ||
                          totalCustomerAmount
                      )}
                    </p>
                    <p className="mb-1">
                      <strong>Partner Discount:</strong> -
                      {formatPrice(
                        invoiceData.amounts?.discount_amount ||
                          totalDiscountAmount
                      )}{" "}
                      ({reseller?.margin || 0}%)
                    </p>
                    <p className="mb-1">
                      <strong>Subtotal:</strong>{" "}
                      {formatPrice(
                        invoiceData.amounts?.partner_amount ||
                          totalPartnerAmount
                      )}
                    </p>
                    <p className="mb-1">
                      <strong>Sales Tax:</strong>{" "}
                      {invoiceData.is_tax_exempt ? (
                        <span className="text-success">$0.00 (Tax Exempt)</span>
                      ) : (
                        formatPrice(invoiceData.amounts?.tax_amount)
                      )}
                    </p>
                    {invoiceData.is_tax_exempt && (
                      <p className="mb-1">
                        <strong>Tax Exemption:</strong>{" "}
                        {invoiceData.tax_exempt_reason}
                      </p>
                    )}
                    <p className="mb-2 fs-6">
                      <strong>Total Due:</strong>{" "}
                      {formatPrice(
                        invoiceData.amounts?.total_due || totalPartnerAmount
                      )}
                    </p>
                    {/* ✅ ADD EMAIL INFORMATION - ALWAYS RESELLER */}
                    <p className="mb-1">
                      <strong>Billing Emails To:</strong>{" "}
                      <span className="text-primary">
                        <CIcon icon={cilEnvelopeClosed} className="me-1" />
                        {getDisplayEmail()}
                      </span>
                      <small className="text-muted ms-2">
                        (ALL billing emails go to reseller)
                      </small>
                    </p>
                    <p className="mb-0 text-info">
                      <strong>
                        Secure ACH and Credit Card payment processing via
                        QuickBooks
                        <br />
                        All billing emails will be sent to the reseller.
                      </strong>
                    </p>
                  </CAlert>
                )}

                {/* DIFFERENT BUTTONS FOR NET TERMS VS ACH AND CREDIT CARD */}
                <CRow className="text-center">
                  <CCol className="mb-3">
                    {paymentTerms !== "immediate" ? (
                      <div>
                        <CButton
                          color="success"
                          className="w-100 mb-2"
                          onClick={handleCloseQuickBooksModal}
                          size="lg"
                        >
                          <CIcon icon={cilCheckCircle} className="me-2" />
                          Done - Subscription Active
                        </CButton>
                        <small className="text-muted d-block">
                          Your subscription is now active. Payment due in{" "}
                          {paymentTerms.replace("net_", "")} days.
                        </small>
                      </div>
                    ) : (
                      <div>
                        <CButton
                          color="primary"
                          className="w-100"
                          onClick={handleDirectPayment}
                          size="lg"
                          disabled={!invoiceData.payment_link}
                        >
                          <CIcon icon={cilDollar} className="me-2" />
                          {invoiceData.payment_link
                            ? "Pay with ACH or Credit Card"
                            : "Processing Payment Link..."}
                        </CButton>
                        <small className="text-muted d-block mt-2">
                          Secure payment via ACH bank transfer or credit card
                        </small>
                      </div>
                    )}
                  </CCol>
                </CRow>
              </CCol>
            </CRow>
          ) : (
            /* PRODUCT SELECTION VIEW */
            <>
              {/* Payment Terms Information - AUTOMATIC */}
              <CRow className="mb-4">
                <CCol>
                  <CCard className="bg-info text-white">
                    <CCardBody>
                      <h6>
                        <CIcon icon={cilDollar} className="me-2" />
                        Automatic Payment Terms
                      </h6>
                      {reseller ? (
                        <>
                          <p className="mb-2">
                            <strong>Reseller Account Payment Mode:</strong> {reseller.payment_mode}
                          </p>
                          <p className="mb-0">
                            <small>
                              This order will be processed using the reseller account's payment terms automatically.
                              <br />
                              <CIcon icon={cilEnvelopeClosed} className="me-1" />
                              <strong>All billing emails will be sent to reseller:</strong> {getDisplayEmail()}
                              {isSuperAdmin && (
                                <span className="text-warning">                        
                                </span>
                              )}
                            </small>
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="mb-2">
                            <strong>Default Payment Mode:</strong> ACH and Credit Card
                          </p>
                          <p className="mb-0">
                            <small>
                              This order will be processed using immediate payment.
                            </small>
                          </p>
                        </>
                      )}
                    </CCardBody>
                  </CCard>
                </CCol>
              </CRow>

              {/* Product Information */}
              <CRow className="mb-4">
                <CCol>
                  <CCard className="bg-light">
                    <CCardBody>
                      <h6>Product Details</h6>
                      <CRow>
                        <CCol md={6}>
                          <p>
                            <strong>Product:</strong>{" "}
                            {selectedProduct?.software_name}
                          </p>
                          <p>
                            <strong>Software ID:</strong>{" "}
                            {selectedProduct?.softwareid}
                          </p>
                          <p>
                            <strong>Type:</strong> {selectedProduct?.type}
                          </p>
                          <p>
                            <strong>Storage Type:</strong>
                            <CBadge
                              color={isUnlimited ? "success" : "info"}
                              className="ms-2"
                            >
                              {isUnlimited ? "Unlimited" : "Limited Storage"}
                            </CBadge>
                          </p>
                        </CCol>
                        <CCol md={6}>
                          <p>
                            <strong>Subscription:</strong>{" "}
                            {selectedProduct?.subscription}
                          </p>
                          <p>
                            <strong>Subscription Type:</strong>{" "}
                            {selectedProduct?.subscription_type}
                          </p>
                          <p>
                            <strong>Client:</strong>{" "}
                            {client?.name || `Client ${clientId}`}
                          </p>
                          <p>
                            <strong>Market:</strong>{" "}
                            <CBadge color="info">{activeTab}</CBadge>
                          </p>
                        </CCol>
                      </CRow>
                    </CCardBody>
                  </CCard>
                </CCol>
              </CRow>

              {/* Support Product Selection */}
              {showSupportSection && (
                <CRow className="mb-4">
                  <CCol>
                    <CCard>
                      <CCardHeader className="bg-info text-white">
                        <h6 className="mb-0">
                          <CIcon icon={cilWarning} className="me-2" />
                          Optional {activeTab.toUpperCase()} Support Products (
                          {availableSupportProducts.length} available)
                        </h6>
                      </CCardHeader>
                      <CCardBody>
                        <CFormCheck
                          type="checkbox"
                          id="includeSupport"
                          label="Add Support Product (Recommended for Perpetual Licenses)"
                          checked={includeSupport}
                          onChange={(e) =>
                            handleSupportToggle(e.target.checked)
                          }
                          className="mb-3"
                        />

                        {includeSupport && (
                          <>
                            <CFormSelect
                              value={selectedSupportProductId}
                              onChange={(e) =>
                                setSelectedSupportProductId(e.target.value)
                              }
                              className="mb-2"
                            >
                              <option value="">
                                Select a support product...
                              </option>
                              {availableSupportProducts.map((support) => (
                                <option
                                  key={support.softwareid}
                                  value={support.softwareid}
                                >
                                  {support.software_name} -{" "}
                                  {support.subscription} -{" "}
                                  {formatPrice(support.price)}
                                </option>
                              ))}
                            </CFormSelect>

                            {selectedSupportProduct && (
                              <div className="mt-2 p-2 bg-light rounded">
                                <small>
                                  <strong>Selected Support:</strong>{" "}
                                  {selectedSupportProduct.subscription}
                                </small>
                              </div>
                            )}
                          </>
                        )}

                        <small className="text-muted">
                          Support products provide maintenance, updates, and
                          technical assistance for perpetual licenses.
                        </small>
                      </CCardBody>
                    </CCard>
                  </CCol>
                </CRow>
              )}

              {/* Cartridge Product Selection */}
              {showCartridgeSection && (
                <CRow className="mb-4">
                  <CCol>
                    <CCard>
                      <CCardHeader className="bg-warning text-white">
                        <h6 className="mb-0">
                          <CIcon icon={cilStorage} className="me-2" />
                          Optional {activeTab.toUpperCase()} Cartridge Products
                          ({availableCartridgeProducts.length} available)
                        </h6>
                      </CCardHeader>
                      <CCardBody>
                        <CFormCheck
                          type="checkbox"
                          id="includeCartridge"
                          label="Add Additional Cartridge Storage"
                          checked={includeCartridge}
                          onChange={(e) =>
                            handleCartridgeToggle(e.target.checked)
                          }
                          className="mb-3"
                        />

                        {includeCartridge && (
                          <>
                            <CFormSelect
                              value={selectedCartridgeProductId}
                              onChange={(e) =>
                                setSelectedCartridgeProductId(e.target.value)
                              }
                              className="mb-2"
                            >
                              <option value="">
                                Select a cartridge product...
                              </option>
                              {availableCartridgeProducts.map((cartridge) => (
                                <option
                                  key={cartridge.softwareid}
                                  value={cartridge.softwareid}
                                >
                                  {cartridge.software_name} -{" "}
                                  {cartridge.subscription} -{" "}
                                  {formatPrice(cartridge.price)}
                                </option>
                              ))}
                            </CFormSelect>

                            {selectedCartridgeProduct && (
                              <div className="mt-2 p-2 bg-light rounded">
                                <small>
                                  <strong>Selected Cartridge:</strong>{" "}
                                  {selectedCartridgeProduct.subscription}
                                </small>
                              </div>
                            )}
                          </>
                        )}

                        <small className="text-muted">
                          Cartridge products provide additional storage capacity
                          for limited storage licenses.
                        </small>
                      </CCardBody>
                    </CCard>
                  </CCol>
                </CRow>
              )}

              {/* Tax Exemption */}
              <CRow className="mb-4">
                <CCol>
                  <CCard>
                    <CCardHeader className="bg-info text-white">
                      <h6 className="mb-0">
                        <CIcon icon={cilWarning} className="me-2" />
                        Tax Exemption
                      </h6>
                    </CCardHeader>
                    <CCardBody>
                      <CFormCheck
                        type="checkbox"
                        id="isTaxExempt"
                        label="This organization is tax exempt (Government, Non-profit, etc.)"
                        checked={isTaxExempt}
                        onChange={(e) => {
                          setIsTaxExempt(e.target.checked);
                          if (!e.target.checked) {
                            setTaxExemptReason("");
                          }
                        }}
                        className="mb-3"
                      />

                      {isTaxExempt && (
                        <>
                          <CFormSelect
                            value={taxExemptReason}
                            onChange={(e) => setTaxExemptReason(e.target.value)}
                            className="mb-2"
                          >
                            <option value="">
                              Select tax exemption reason...
                            </option>
                            <option value="Government Organization">
                              Government Organization
                            </option>
                            <option value="Non-profit Organization">
                              Non-profit Organization
                            </option>
                            <option value="Educational Institution">
                              Educational Institution
                            </option>
                            <option value="Religious Organization">
                              Religious Organization
                            </option>
                            <option value="Other Tax Exempt">
                              Other Tax Exempt Entity
                            </option>
                          </CFormSelect>

                          {taxExemptReason && (
                            <div className="mt-2 p-2 bg-light rounded">
                              <small>
                                <strong>Tax Exempt:</strong> {taxExemptReason}
                              </small>
                              <br />
                              <small className="text-success">
                                ✅ No sales tax will be applied to this invoice
                              </small>
                            </div>
                          )}
                        </>
                      )}
                    </CCardBody>
                  </CCard>
                </CCol>
              </CRow>

              {/* Pricing Breakdown */}
              <CRow className="mb-4">
                <CCol>
                  <h6>Pricing Breakdown</h6>
                  {reseller && (
                    <CAlert color="info" className="mb-3">
                      <small>
                        Reseller Discount: <strong>{reseller.margin}%</strong>{" "}
                        applied to all amounts
                        <br />
                        <CIcon icon={cilEnvelopeClosed} className="me-1" />
                        Billing Emails: <strong>{getDisplayEmail()}</strong>
                        {isSuperAdmin && (
                          <span className="text-warning ms-1">                          
                          </span>
                        )}
                      </small>
                    </CAlert>
                  )}
                  {!reseller && (
                    <CAlert color="warning" className="mb-3">
                      <small>
                        No reseller discount information available
                      </small>
                    </CAlert>
                  )}
                  <CTable striped bordered responsive>
                    <CTableHead className="bg-light">
                      <CTableRow>
                        <CTableHeaderCell>Description</CTableHeaderCell>
                        <CTableHeaderCell className="text-end">
                          Customer Amount
                        </CTableHeaderCell>
                        <CTableHeaderCell className="text-end">
                          Partner Amount
                        </CTableHeaderCell>
                        <CTableHeaderCell className="text-end">
                          Discount
                        </CTableHeaderCell>
                      </CTableRow>
                    </CTableHead>
                    <CTableBody>
                      {/* Main Product */}
                      <CTableRow>
                        <CTableDataCell>
                          <strong>{selectedProduct?.software_name}</strong>
                          <br />
                          <small className="text-muted">
                            {selectedProduct?.subscription}
                          </small>
                        </CTableDataCell>
                        <CTableDataCell className="text-end">
                          <strong>{formatPrice(selectedProduct?.price)}</strong>
                        </CTableDataCell>
                        <CTableDataCell className="text-end">
                          <strong className="text-success">
                            {formatPrice(
                              calculatePartnerAmount(selectedProduct?.price)
                            )}
                          </strong>
                        </CTableDataCell>
                        <CTableDataCell className="text-end">
                          <small className="text-danger">
                            {formatPrice(
                              calculateDiscountAmount(selectedProduct?.price)
                            )}
                          </small>
                        </CTableDataCell>
                      </CTableRow>

                      {/* Support Product */}
                      {includeSupport && selectedSupportProduct && (
                        <CTableRow>
                          <CTableDataCell>
                            <strong>
                              Support: {selectedSupportProduct.software_name}
                            </strong>
                            <br />
                            <small className="text-muted">
                              {selectedSupportProduct.subscription}
                            </small>
                          </CTableDataCell>
                          <CTableDataCell className="text-end">
                            <strong>
                              {formatPrice(selectedSupportProduct.price)}
                            </strong>
                          </CTableDataCell>
                          <CTableDataCell className="text-end">
                            <strong className="text-success">
                              {formatPrice(
                                calculatePartnerAmount(
                                  selectedSupportProduct.price
                                )
                              )}
                            </strong>
                          </CTableDataCell>
                          <CTableDataCell className="text-end">
                            <small className="text-danger">
                              {formatPrice(
                                calculateDiscountAmount(
                                  selectedSupportProduct.price
                                )
                              )}
                            </small>
                          </CTableDataCell>
                        </CTableRow>
                      )}

                      {/* Cartridge Product */}
                      {includeCartridge && selectedCartridgeProduct && (
                        <CTableRow>
                          <CTableDataCell>
                            <strong>
                              Cartridge:{" "}
                              {selectedCartridgeProduct.software_name}
                            </strong>
                            <br />
                            <small className="text-muted">
                              {selectedCartridgeProduct.subscription}
                            </small>
                          </CTableDataCell>
                          <CTableDataCell className="text-end">
                            <strong>
                              {formatPrice(selectedCartridgeProduct.price)}
                            </strong>
                          </CTableDataCell>
                          <CTableDataCell className="text-end">
                            <strong className="text-success">
                              {formatPrice(
                                calculatePartnerAmount(
                                  selectedCartridgeProduct.price
                                )
                              )}
                            </strong>
                          </CTableDataCell>
                          <CTableDataCell className="text-end">
                            <small className="text-danger">
                              {formatPrice(
                                calculateDiscountAmount(
                                  selectedCartridgeProduct.price
                                )
                              )}
                            </small>
                          </CTableDataCell>
                        </CTableRow>
                      )}

                      {/* Total Row */}
                      <CTableRow className="table-primary">
                        <CTableDataCell>
                          <strong>TOTAL PAYABLE AMOUNT</strong>
                          <br />
                          <small className="text-muted">
                            Amount to be charged {isTaxExempt && "(Tax Exempt)"}
                          </small>
                          {isTaxExempt && (
                            <div>
                              <small className="text-success">
                                ✅ Tax will be excluded
                              </small>
                            </div>
                          )}
                          {!isTaxExempt && (
                            <div>
                              <small className="text-muted">
                                📍 Tax will be calculated by QuickBooks
                              </small>
                            </div>
                          )}
                        </CTableDataCell>
                        <CTableDataCell className="text-end">
                          <strong className="text-primary fs-5">
                            {formatPrice(totalCustomerAmount)}
                          </strong>
                        </CTableDataCell>
                        <CTableDataCell className="text-end">
                          <strong className="text-success fs-5">
                            {formatPrice(totalPartnerAmount)}
                          </strong>
                        </CTableDataCell>
                        <CTableDataCell className="text-end">
                          <strong className="text-danger">
                            {formatPrice(totalDiscountAmount)}
                          </strong>
                        </CTableDataCell>
                      </CTableRow>
                    </CTableBody>
                  </CTable>

                  {/* Renewal Information */}
                  {(includeSupport || includeCartridge || isUnlimited) && (
                    <CCard className="mt-3 bg-light">
                      <CCardBody>
                        <h6>Renewal Information</h6>
                        <CRow>
                          <CCol md={6}>
                            <p className="mb-1">
                              <strong>Next Renewal Date:</strong>{" "}
                              {new Date(
                                Date.now() + 365 * 24 * 60 * 60 * 1000
                              ).toLocaleDateString()}
                            </p>
                            <p className="mb-1">
                              <strong>Renewal Amount (Customer):</strong>{" "}
                              {formatPrice(
                                calculateRenewalAmount(
                                  selectedProduct,
                                  includeSupport && selectedSupportProduct
                                    ? selectedSupportProduct
                                    : null,
                                  includeCartridge && selectedCartridgeProduct
                                    ? selectedCartridgeProduct
                                    : null
                                )
                              )}
                            </p>
                          </CCol>
                          <CCol md={6}>
                            <p className="mb-1">
                              <strong>Renewal Amount (Partner):</strong>{" "}
                              {formatPrice(
                                calculatePartnerAmount(
                                  calculateRenewalAmount(
                                    selectedProduct,
                                    includeSupport && selectedSupportProduct
                                      ? selectedSupportProduct
                                      : null,
                                    includeCartridge && selectedCartridgeProduct
                                      ? selectedCartridgeProduct
                                      : null
                                  )
                                )
                              )}
                            </p>
                            <p className="mb-0">
                              <strong>Renewal Type:</strong>{" "}
                              {isUnlimited
                                ? includeSupport || includeCartridge
                                  ? "Addons Only"
                                  : "Full Product"
                                : includeSupport || includeCartridge
                                ? "Addons Only"
                                : "One-time Product"}
                            </p>
                          </CCol>
                        </CRow>
                      </CCardBody>
                    </CCard>
                  )}
                </CCol>
              </CRow>

              {quickbooksError && (
                <CAlert color="danger" className="mt-3">
                  {quickbooksError}
                </CAlert>
              )}
            </>
          )}
        </CModalBody>
        <CModalFooter>
          {!invoiceData ? (
            <>
              <CButton
                color="secondary"
                onClick={handleCloseQuickBooksModal}
                disabled={quickbooksLoading}
              >
                Cancel
              </CButton>
              <CButton
                color="primary"
                onClick={handleQuickBooksSubmit}
                disabled={
                  quickbooksLoading ||
                  !client ||
                  (includeSupport && !selectedSupportProductId) ||
                  (includeCartridge && !selectedCartridgeProductId)
                }
              >
                {quickbooksLoading ? (
                  <>
                    <CSpinner size="sm" className="me-2" />
                    Creating Order...
                  </>
                ) : (
                  `Create Order with ${reseller?.payment_mode || "ACH and Credit Card"}`
                )}
              </CButton>
            </>
          ) : (
            <CButton color="secondary" onClick={handleCloseQuickBooksModal}>
              Close
            </CButton>
          )}
        </CModalFooter>
      </CModal>
    </CContainer>
  );
};

export default ClientProducts;